/**
 * 任務補登頁（LINE@ 司機端）
 *
 * v1 範圍：
 *   - 載入該司機的補登清單
 *   - 三入口：
 *     1. 「自發補登」（SELF_LINEAT）→ 開表單 Dialog
 *     2. 「完成被指派」(COMPLETE_ASSIGNED) → 點 PENDING_FILL 列表項打開完成 Dialog
 *     3. 「掃描貨櫃」(FROM_TASK_Q) → 輸入貨櫃號碼查 TASK_Q → 找到帶入表單
 *   - 打卡紀錄選擇器（依司機自身查 daily_report）
 *
 * v2 待補：雙拖 UI / 真正鏡頭掃描元件（目前 TASK_Q 入口為手動輸入）
 */

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import {
  AppBar,
  Toolbar,
  IconButton,
  Typography,
  Container,
  Box,
  Paper,
  Button,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Stack,
  Divider,
  Checkbox,
  FormControlLabel,
  InputAdornment,
  ListSubheader,
  Switch,
} from '@mui/material'
import EditNoteIcon from '@mui/icons-material/EditNote'
import FormatListBulletedIcon from '@mui/icons-material/FormatListBulleted'
import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import QrCodeScannerIcon from '@mui/icons-material/QrCodeScanner'
import RefreshIcon from '@mui/icons-material/Refresh'
import SearchIcon from '@mui/icons-material/Search'
import CameraAltIcon from '@mui/icons-material/CameraAlt'
import LocalShippingIcon from '@mui/icons-material/LocalShipping'
import DirectionsBoatIcon from '@mui/icons-material/DirectionsBoat'
import Inventory2Icon from '@mui/icons-material/Inventory2'
import PlaceIcon from '@mui/icons-material/Place'
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined'
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline'

import { useLiff } from '@/contexts/LiffContext'
import { getDriver } from '@/services/driverService'
import { missionAmendmentService } from '@/services/missionAmendmentService'
import { ApiBusinessError } from '@/services/apiClient'
import { getCompanyVehicles, getCurrentDuty } from '@/services/vehicleService'
import PickerField from '@/components/PickerField'
import MobilePicker from '@/components/MobilePicker'
import BarcodeScanner from '@/components/BarcodeScanner'
import type { Driver } from '@/types'
import {
  AMENDMENT_MODE_LABEL,
  AMENDMENT_STATUS_LABEL,
  type AmendmentMode,
  type AmendmentStatus,
  type DailyReportListItem,
  type MissionAmendment,
  type TaskQItem,
} from '@/types/MissionAmendment'

type DialogMode = 'self' | 'complete' | 'taskq' | null

/** 顯示用 mapping — 把後端代碼轉成司機友善文字 */
const formatContainerSize = (v: string | null | undefined) => v ? `${v} 呎` : '-'
const formatEmptyLaden = (v: string | null | undefined) =>
  v === 'L' ? '重櫃' : v === 'E' ? '空櫃' : '-'
const formatIoType = (v: string | null | undefined) =>
  v === 'E' ? '出口' : v === 'I' ? '進口' : (v || '-')

/** 詳情 Dialog 內單列 label/value（手機友善：左 label、右 value） */
const DetailRow: React.FC<{ label: string; value: React.ReactNode }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 2 }}>
    <Typography variant="body2" sx={{ color: '#666', minWidth: 84 }}>{label}</Typography>
    <Typography variant="body2" sx={{ color: '#222', fontWeight: 500, textAlign: 'right', wordBreak: 'break-all' }}>
      {value}
    </Typography>
  </Box>
)

interface AmendmentFormState {
  amendmentId?: string  // 完成被指派時使用
  sourceTaskQId?: string | null  // FROM_TASK_Q 時用
  dailyReportId: string
  trailerCoCd: string
  trailerNo: string
  /** v2 #5：拖運日期（YYYY-MM-DD） */
  dutyDate: string
  vesselRegNo: string
  /** v2.3：補登用船名 fallback — 船掛存在 vessel_work 時 readonly 顯示真實船名 */
  amendmentVslName: string
  containerNo: string
  containerSize: '20' | '40' | '45' | ''
  emptyLaden: 'E' | 'L' | ''
  containerWeight: string  // 字串以利 input
  originLocation: string
  destinationLocation: string
  billingRequester: string
  // 雙拖
  doubleTowFlag: boolean
  containerNo2: string
  containerWeight2: string
}

// v2 #5：dutyDate 預設今天，pickDailyReport 時自動同步打卡日
const todayStr = (): string => {
  const d = new Date()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${d.getFullYear()}-${m}-${day}`
}

const EMPTY_FORM: AmendmentFormState = {
  amendmentId: undefined,
  sourceTaskQId: undefined,
  dailyReportId: '',
  trailerCoCd: '',
  trailerNo: '',
  dutyDate: todayStr(),
  vesselRegNo: '',
  amendmentVslName: '',
  containerNo: '',
  containerSize: '',
  emptyLaden: '',
  containerWeight: '',
  originLocation: '',
  destinationLocation: '',
  billingRequester: '',
  doubleTowFlag: false,
  containerNo2: '',
  containerWeight2: '',
}

/** 雙拖列表用：櫃號前小圓編號 chip（淺灰底白字 ①②） */
const NumChip: React.FC<{ n: 1 | 2 }> = ({ n }) => (
  <Box component="span" sx={{
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 16, height: 16,
    borderRadius: '50%',
    backgroundColor: '#757575',
    color: 'white',
    fontSize: 10,
    fontWeight: 700,
    flexShrink: 0,
    mr: 0.5,
  }}>{n}</Box>
)

/**
 * v2.3.x：補登 form 分區卡片
 * 白底圓角 + 標題 icon，與 LINE@ 棕綠系一致；用 outlined Paper 不要太搶眼
 */
const SectionCard: React.FC<{
  title: string
  icon: React.ReactNode
  children: React.ReactNode
}> = ({ title, icon, children }) => (
  <Paper
    variant="outlined"
    sx={{ p: 2, borderRadius: 2, borderColor: '#e0e0d8', backgroundColor: 'white' }}
  >
    <Stack direction="row" alignItems="center" gap={1} sx={{ mb: 1.5, color: '#6B705C' }}>
      {icon}
      <Typography variant="subtitle2" fontWeight={700}>{title}</Typography>
    </Stack>
    <Stack spacing={2}>{children}</Stack>
  </Paper>
)

/** Chip 配色 — 對齊「交派任務(驥達)」開工狀態色系（棕 / 灰藍 / 深綠 + 紅） */
const STATUS_CHIP_SX: Record<AmendmentStatus, { backgroundColor: string; color: string }> = {
  PENDING_FILL:   { backgroundColor: '#CB997E', color: '#fff' },  // 待填寫（棕）
  PENDING_REVIEW: { backgroundColor: '#6C9BD1', color: '#fff' },  // 待審核（灰藍）
  APPROVED:       { backgroundColor: '#6B705C', color: '#fff' },  // 已補登（深綠）
  REJECTED:       { backgroundColor: '#d32f2f', color: '#fff' },  // 已退件（紅）
  RETURNED:       { backgroundColor: '#F4A261', color: '#fff' },  // v2.3 退回重填（橘）
}

// 將 API 錯誤轉成司機看得懂的訊息：
//   - 業務錯誤（ApiBusinessError）優先用後端 statusDesc
//   - HTTP 錯誤優先用後端 statusDesc/message；409 一律視為「重複」，給明確引導
//   - 都取不到才用 fallback；避免把 raw「AxiosError: Request failed with status code 409」丟給使用者
const DUPLICATE_HINT = '這筆補登資料重複，可能先前已送出過。請至下方「我的補登」清單確認，不需重複送出。'
const describeApiError = (err: unknown, fallback: string): string => {
  // 後端業務錯誤（HTTP 200 + statusCode!==200）→ apiClient 已轉成 ApiBusinessError，statusDesc 即友善訊息（如 DSP-1331）
  if (err instanceof ApiBusinessError) return err.statusDesc || fallback
  if (err && typeof err === 'object' && 'response' in err) {
    const ax = err as {
      response?: { status?: number; data?: { statusCode?: string; statusDesc?: string; message?: string } }
    }
    const data = ax.response?.data
    const apiMsg = data?.statusDesc || data?.message
    if (ax.response?.status === 409) {
      // DB unique 防禦路徑回的通用「資料重複，無法新增」(statusCode==="409") 太籠統 → 改用友善引導；
      // 具體業務碼（DSP-xxxx / LINE-xxxx）帶的訊息才直接顯示
      const isGeneric = !apiMsg || data?.statusCode === '409'
      return isGeneric ? DUPLICATE_HINT : apiMsg
    }
    if (apiMsg) return apiMsg
  }
  return fallback
}

const MissionAmendmentPage: React.FC = () => {
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  const { lineUser } = useLiff()

  const [driver, setDriver] = useState<Driver | null>(null)
  const [list, setList] = useState<MissionAmendment[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  // v2.3：保留目前編輯的 amendment（用於 RETURNED 狀態下顯示「退回原因」banner）
  const [editingItem, setEditingItem] = useState<MissionAmendment | null>(null)
  // 已完成（APPROVED / REJECTED）預設隱藏 — 司機 90% 場景關心未完成
  const [showCompleted, setShowCompleted] = useState(false)

  // 即時 inline 檢核 hint — 櫃號 ISO 6346
  type Hint = { level: 'error' | 'warn' | 'ok'; text: string }
  const [containerNoHint, setContainerNoHint] = useState<Hint | null>(null)
  const [containerNo2Hint, setContainerNo2Hint] = useState<Hint | null>(null)
  const HINT_COLOR: Record<Hint['level'], string> = {
    error: '#d32f2f',
    warn: '#ed6c02',
    ok: '#198754',
  }
  // 櫃號 onBlur → 後端 ISO 6346 檢核
  const computeContainerNoHint = useCallback(async (value: string): Promise<Hint | null> => {
    if (!value || !value.trim()) return null
    try {
      await missionAmendmentService.validateContainerNo(value.trim())
      return { level: 'ok', text: '貨櫃號碼檢核通過' }
    } catch (e: unknown) {
      const err = e as { code?: string }
      if (err.code === 'DSP-0706') return { level: 'error', text: '格式錯誤：需為 4 碼英文 + 7 碼數字（共 11 碼）' }
      if (err.code === 'DSP-0707') return { level: 'warn', text: '檢核碼不符（仍可儲存，但請確認櫃號是否正確）' }
      return null
    }
  }, [])

  // 通用確認 Dialog —「是 / 否」確認用（送出前 ISO 6346 檢核碼軟警示）
  const [confirmDialog, setConfirmDialog] = useState<{ open: boolean; title: string; text: string }>(
    { open: false, title: '', text: '' },
  )
  const confirmResolveRef = useRef<((ok: boolean) => void) | null>(null)
  const confirmYesNo = (title: string, text: string): Promise<boolean> => {
    setConfirmDialog({ open: true, title, text })
    return new Promise<boolean>((resolve) => {
      confirmResolveRef.current = resolve
    })
  }
  const handleConfirmDialogClose = (ok: boolean) => {
    setConfirmDialog((d) => ({ ...d, open: false }))
    const r = confirmResolveRef.current
    confirmResolveRef.current = null
    if (r) r(ok)
  }

  const [dialogMode, setDialogMode] = useState<DialogMode>(null)
  // 送出/儲存/檢核失敗時，錯誤 Alert 在對話框頂端；但送出鈕在長表單底部，
  // 使用者往往看不到 → 對話框開啟時只要出現 error 就自動捲回頂端，讓提示可見。
  const formDialogContentRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    if (error && dialogMode !== null) {
      formDialogContentRef.current?.scrollTo({ top: 0, behavior: 'smooth' })
    }
  }, [error, dialogMode])
  // 送出/儲存失敗時額外跳一個 modal 強提示（按鈕在頂/底部，僅頂端 Alert 易被忽略）
  const [errorDialog, setErrorDialog] = useState<{ title: string; message: string } | null>(null)
  // 詳情檢視 — 非 PENDING_FILL（送審/已補登/退件）也能看完整內容
  const [viewingAmendment, setViewingAmendment] = useState<MissionAmendment | null>(null)
  const [form, setForm] = useState<AmendmentFormState>(EMPTY_FORM)
  const [formErrors, setFormErrors] = useState<Record<string, string>>({})
  interface VesselInfo { vslName?: string; shipCompanies: Array<{ value: string; label: string }> }
  const [vesselInfo, setVesselInfo] = useState<VesselInfo | null>(null)

  // 起迄地 cascade（縣市 → 地點；2026-09 客戶決議：不依儲位類型 T/C/Y 過濾，與 WEB 補登一致）
  interface LocationOption {
    value: string
    label: string
    shortName?: string
    areaCode?: string
    storageLocationTypes?: string[]
  }
  const [areaCodeOptions, setAreaCodeOptions] = useState<Array<{ code: string; name: string }>>([])
  const [fromAreaCode, setFromAreaCode] = useState('')
  const [toAreaCode, setToAreaCode] = useState('')
  const [fromLocationOptions, setFromLocationOptions] = useState<LocationOption[]>([])
  const [toLocationOptions, setToLocationOptions] = useState<LocationOption[]>([])
  // v2.3：委託人下拉（取代原本以 vesselInfo.shipCompanies 為來源）
  const [consignorOptions, setConsignorOptions] = useState<Array<{ value: string; label: string }>>([])
  // v2.3：從清單選 picker 狀態
  const [consignorPickerOpen, setConsignorPickerOpen] = useState(false)
  // v2.3：手動輸入委託人 dialog 狀態
  const [manualConsignorOpen, setManualConsignorOpen] = useState(false)
  const [manualConsignorDraft, setManualConsignorDraft] = useState('')
  // v2.3.x：委託人「選擇方式」selector dialog（點 input/筆 icon 觸發）
  const [consignorSelectorOpen, setConsignorSelectorOpen] = useState(false)
  // v2.3.x：移除「打卡紀錄」UI，改：
  //   - 車號改下拉（司機所屬公司全部車輛）
  //   - 拖運日期：自己選 / 從過往打卡日期選（仿委託人 selector pattern）
  const [vehicleOptions, setVehicleOptions] = useState<Array<{ value: string; label: string }>>([])
  const [currentDutyDate, setCurrentDutyDate] = useState<string>('')   // 當前班別日期（mount 取一次當預設）
  const [dutyDateSelectorOpen, setDutyDateSelectorOpen] = useState(false)
  const [manualDutyDateOpen, setManualDutyDateOpen] = useState(false)
  const [manualDutyDateDraft, setManualDutyDateDraft] = useState('')
  const [vehiclePickerOpen, setVehiclePickerOpen] = useState(false)

  // 載入縣市別
  useEffect(() => {
    missionAmendmentService.listCityAreaCodes()
      .then(setAreaCodeOptions)
      .catch(() => setAreaCodeOptions([]))
  }, [])

  // v2.3.x：「dialog 開啟 token」— 每次進入新增/編輯/補完，所有關聯資料都重新連結
  //   1. driverDailyReports（用於日期+車號 → dailyReportId 自動連結）
  //   2. consignorOptions / vehicleOptions / currentDutyDate
  //   3. fromLocationOptions / toLocationOptions
  //   宣告於最上方，讓後續所有依賴它的 useEffect 都能 reference
  const [dialogResyncToken, setDialogResyncToken] = useState(0)

  // v2.3：載入委託人候選清單 — 司機所屬拖車公司 = supplierCompany
  //   依賴 dialogResyncToken 確保每次進入編輯都重新拉，避免舊清單跟 form 對不上 label
  useEffect(() => {
    const supplier = driver?.companyCode || null
    missionAmendmentService.listConsignorOptions(supplier)
      .then((items) => setConsignorOptions(items.map((i) => ({ value: i.code, label: i.name }))))
      .catch((err) => {
        console.warn('載入委託人下拉失敗', err)
        setConsignorOptions([])
      })
  }, [driver?.companyCode, dialogResyncToken])

  // v2.3.x：載入司機公司全部車輛（作為車號下拉選項）
  useEffect(() => {
    if (!driver?.companyName) return
    getCompanyVehicles(driver.companyName)
      .then((vehicles) => setVehicleOptions(vehicles.map((v) => ({ value: v.vehicleNo, label: v.vehicleNo }))))
      .catch((err) => {
        console.warn('載入車輛清單失敗', err)
        setVehicleOptions([])
      })
  }, [driver?.companyName, dialogResyncToken])

  // v2.3.x：載入司機「當前班別日期」— mount 取一次作為新增補登預設值
  useEffect(() => {
    if (!driver) return
    getCurrentDuty()
      .then((duty) => setCurrentDutyDate(duty?.reportDate ?? ''))
      .catch(() => setCurrentDutyDate(''))
  }, [driver, dialogResyncToken])

  // v2.3.x：載入司機所有打卡紀錄（client-side 比對「日期+車號」是否真有對應打卡）
  // - driverDailyReportsLoaded 旗標確保「載入完成才比對」，避免初始空陣列把編輯資料的 dailyReportId 誤清掉
  // - dialogResyncToken 變化也會 re-fetch，確保進入編輯時拿到最新打卡（可能司機剛打了新班）
  const [driverDailyReports, setDriverDailyReports] = useState<DailyReportListItem[]>([])
  const [driverDailyReportsLoaded, setDriverDailyReportsLoaded] = useState(false)
  useEffect(() => {
    if (!driver) return
    setDriverDailyReportsLoaded(false)
    missionAmendmentService.searchDailyReportsByDriver({ driverId: driver.driverId })
      .then((data) => {
        setDriverDailyReports(data)
        setDriverDailyReportsLoaded(true)
      })
      .catch(() => {
        setDriverDailyReports([])
        setDriverDailyReportsLoaded(true)
      })
  }, [driver, dialogResyncToken])

  // v2.3.x：dutyDate / trailerNo 變動時，比對司機打卡紀錄（含進入編輯畫面時）
  //   - 找到匹配 → 自動 setForm dailyReportId（顯示「已連結至打卡紀錄」）
  //   - 找不到 → dailyReportId 清空（顯示「無對應打卡」）
  //   - **重要**：必須等 driverDailyReportsLoaded 才動作，否則初始空陣列會誤清編輯資料的 dailyReportId
  useEffect(() => {
    if (!driverDailyReportsLoaded) return        // 還在載入 → 不動作，避免誤清
    if (!form.dutyDate || !form.trailerNo) return // 條件不足 → 不動作
    const match = driverDailyReports.find(
      (r) => r.reportDate === form.dutyDate && r.vehicleNo === form.trailerNo,
    )
    setForm((f) => {
      const nextId = match?.dailyReportId ?? ''
      if (f.dailyReportId === nextId) return f
      return { ...f, dailyReportId: nextId }
    })
  }, [form.dutyDate, form.trailerNo, driverDailyReports, driverDailyReportsLoaded])

  // 起點 cascade — 依 fromAreaCode + dialogResyncToken（每次進編輯都重拉）
  //   不依空重／儲位類型過濾：該縣市所有啟用地點皆可選
  useEffect(() => {
    if (!fromAreaCode) { setFromLocationOptions([]); return }
    let cancelled = false
    missionAmendmentService.listLocationsByArea(fromAreaCode).then((opts) => {
      if (!cancelled) setFromLocationOptions(opts)
    }).catch(() => { if (!cancelled) setFromLocationOptions([]) })
    return () => { cancelled = true }
  }, [fromAreaCode, dialogResyncToken])

  // 迄點 cascade — 依 toAreaCode + dialogResyncToken
  useEffect(() => {
    if (!toAreaCode) { setToLocationOptions([]); return }
    let cancelled = false
    missionAmendmentService.listLocationsByArea(toAreaCode).then((opts) => {
      if (!cancelled) setToLocationOptions(opts)
    }).catch(() => { if (!cancelled) setToLocationOptions([]) })
    return () => { cancelled = true }
  }, [toAreaCode, dialogResyncToken])
  const [submitting, setSubmitting] = useState(false)
  const [savedHint, setSavedHint] = useState<string | null>(null)

  const [pickerOpen, setPickerOpen] = useState(false)
  const [pickerResults, setPickerResults] = useState<DailyReportListItem[]>([])
  const [pickerLoading, setPickerLoading] = useState(false)

  const [taskQDialogOpen, setTaskQDialogOpen] = useState(false)
  const [taskQContainerInput, setTaskQContainerInput] = useState('')
  const [taskQResults, setTaskQResults] = useState<TaskQItem[]>([])
  const [taskQLoading, setTaskQLoading] = useState(false)
  const [taskQSearched, setTaskQSearched] = useState(false)  // 區分「未搜尋」與「查無資料」
  const [scannerOpen, setScannerOpen] = useState(false)
  // 表單內掃描器（船掛 + 櫃號），與 TASK_Q 入口的 scannerOpen 區分
  const [formScannerOpen, setFormScannerOpen] = useState(false)

  // 表單欄位修正後自動清掉對應 inline 錯誤訊息
  useEffect(() => {
    setFormErrors((prev) => {
      if (Object.keys(prev).length === 0) return prev
      const next = { ...prev }
      if (form.dailyReportId && next.dailyReportId) delete next.dailyReportId
      if (form.vesselRegNo && next.vesselRegNo) delete next.vesselRegNo
      if (form.containerNo && next.containerNo) delete next.containerNo
      if (form.originLocation && next.originLocation) delete next.originLocation
      if (form.destinationLocation && next.destinationLocation) delete next.destinationLocation
      if (next.containerSize && (!form.doubleTowFlag || form.containerSize === '20')) delete next.containerSize
      if (next.containerNo2 && (!form.doubleTowFlag || (form.containerNo2 && form.containerNo2 !== form.containerNo))) delete next.containerNo2
      return next
    })
  }, [form])

  // ====================================================================
  // 載入司機 + 補登清單
  // ====================================================================

  const loadList = useCallback(async (driverId: string) => {
    setLoading(true)
    setError(null)
    try {
      const data = await missionAmendmentService.listByDriver(driverId)
      setList(data)
    } catch (err) {
      console.error('載入補登清單失敗', err)
      setError('載入補登清單失敗')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    const init = async () => {
      if (!lineUser?.userId) {
        setError('請先綁定 LINE 帳號')
        setLoading(false)
        return
      }
      try {
        const d = await getDriver(lineUser.userId)
        if (!d) {
          setError('找不到對應司機資料')
          setLoading(false)
          return
        }
        setDriver(d)
        await loadList(d.driverId)
      } catch (err) {
        console.error('初始化失敗', err)
        setError('初始化失敗')
        setLoading(false)
      }
    }
    init()
  }, [lineUser, loadList])

  // LINE 推播深連結 — 進入 /mission-amendment?id=xxx 時自動開啟對應補登
  //   PENDING_FILL → 直接進「完成被指派」表單（補欄位）
  //   其他狀態 → 開詳情 Dialog 檢視
  useEffect(() => {
    const id = searchParams.get('id')
    if (!id || list.length === 0) return
    const target = list.find((x) => x.id === id)
    if (!target) return
    // v2.3：RETURNED 同 PENDING_FILL，可走補完流程
    if (target.status === 'PENDING_FILL' || target.status === 'RETURNED') {
      startCompleteAssigned(target)
    } else {
      setViewingAmendment(target)
    }
    // 消費後清掉 query string，避免再次觸發
    setSearchParams({}, { replace: true })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [list, searchParams])

  // ====================================================================
  // 打卡選擇器（依司機）
  // ====================================================================

  const openPicker = async () => {
    if (!driver) return
    setPickerOpen(true)
    setPickerLoading(true)
    try {
      const data = await missionAmendmentService.searchDailyReportsByDriver({
        driverId: driver.driverId,
      })
      setPickerResults(data)
    } catch (err) {
      console.error('搜尋打卡紀錄失敗', err)
    } finally {
      setPickerLoading(false)
    }
  }

  // v2.3.x：原「打卡紀錄」TextField 已移除，pickedDailyReportDisplay 不再顯示，
  // 但 reset / pickDailyReport / manualDutyDate 內仍維持 setter（後續若還原 UI 不會壞）
  const [, setPickedDailyReportDisplay] = useState('')

  const pickDailyReport = (item: DailyReportListItem) => {
    setForm((f) => ({
      ...f,
      dailyReportId: item.dailyReportId,
      trailerNo: item.vehicleNo,
      trailerCoCd: item.companyCd,
      // v2 #5：選打卡同時更新拖運日期，使用者仍可手動覆蓋
      dutyDate: item.reportDate,
    }))
    // 顯示「司機 ／ 車號 ／ 班別日期」
    const driver = item.driverName ?? '-'
    setPickedDailyReportDisplay(
      `${driver} ／ ${item.vehicleNo} ／ ${item.reportDate}`,
    )
    setPickerOpen(false)
  }

  // ====================================================================
  // 三個入口
  // ====================================================================

  /**
   * 船掛 onBlur → 查 vesselWork
   *
   * v2.3：查無此船掛**不再**標示為 error（紅框/紅字）— v2 #8 以後不存在的船掛
   * 是合理場景（W+seq fallback / 補登歷史任務），系統會引導使用者改填「船名」欄位。
   * 僅在「lookup 網路失敗」等真錯誤時顯示紅字。
   */
  const handleVesselBlur = async (vslRegNo: string) => {
    setVesselInfo(null)
    setFormErrors((prev) => ({ ...prev, vesselRegNo: '' }))
    if (!vslRegNo || vslRegNo.length !== 6) return
    try {
      const result = await missionAmendmentService.lookupVessel(vslRegNo)
      if (!result) {
        // 查無：靜默處理 — vesselInfo 維持 null，船名欄位自動切到「可手填」模式
        return
      }
      const companies = result.shipCompanies ?? []
      setVesselInfo({ vslName: result.vslName, shipCompanies: companies })
      // v2.3：委託人來源已脫鉤 — 改由 consignorOptions（拖車公司範疇）提供，
      // 不再因船掛 lookup 自動帶/清空 form.billingRequester
    } catch (err) {
      console.warn('查詢船掛失敗', err)
      setFormErrors((prev) => ({ ...prev, vesselRegNo: '查詢船掛失敗' }))
    }
  }

  /** 1. 自發補登 SELF_LINEAT */
  /** 重置整個補登 dialog 相關狀態 — 避免跨入口殘留 */
  // v2 需求 #3：起迄縣市預設「基隆」（area_code='017'）；
  //   開啟既有 amendment 編輯時，呼叫端會在 reset 後覆寫回實際 areaCode
  const DEFAULT_AREA_CODE = '017'
  const resetAmendmentDialogState = () => {
    setFormErrors({})
    setVesselInfo(null)
    setPickedDailyReportDisplay('')
    setEditingItem(null)   // v2.3：清掉編輯項目（reset 時）
    setFromAreaCode(DEFAULT_AREA_CODE)
    setToAreaCode(DEFAULT_AREA_CODE)
    setFromLocationOptions([])
    setToLocationOptions([])
    setSavedHint(null)
    setError(null)
    setContainerNoHint(null)
    setContainerNo2Hint(null)
  }

  const startSelfAmendment = () => {
    if (!driver) return
    // v2.3.x：預設拖運日期 = 當前班別日期；車號 = 當前班別車輛
    setForm({
      ...EMPTY_FORM,
      trailerCoCd: driver.companyCode,
      dutyDate: currentDutyDate || todayStr(),
      trailerNo: driver.currentVehicleNo ?? '',
    })
    resetAmendmentDialogState()
    setDialogResyncToken((t) => t + 1)   // v2.3.x：force re-resolve 所有關聯
    setDialogMode('self')
  }

  /** 2. 點 PENDING_FILL 列表項 → 完成被指派的補登 */
  const startCompleteAssigned = (item: MissionAmendment) => {
    setForm({
      ...EMPTY_FORM,
      amendmentId: item.id,
      trailerCoCd: item.trailerCoCd,
      trailerNo: item.trailerNo ?? '',
      // v2 #5：殼如已預填 dutyDate 就用，否則用 dailyReportDate 或今天
      dutyDate: item.dutyDate ?? item.dailyReportDate ?? todayStr(),
      vesselRegNo: item.vesselRegNo,
      // v2.3：殼如有預填 amendmentVslName 就帶入
      amendmentVslName: item.amendmentVslName ?? '',
      containerNo: item.containerNo,
      containerSize: (item.containerSize as '20' | '40' | '45') ?? '20',
      emptyLaden: (item.emptyLaden as 'E' | 'L') ?? 'L',
      containerWeight: item.containerWeight != null ? String(item.containerWeight) : '',
      originLocation: item.originLocation,
      destinationLocation: item.destinationLocation,
      billingRequester: item.billingRequester,
      // 雙拖三欄位需保留：調派建殼時若已勾雙拖，司機補完時必須延續，否則第二櫃資訊會遺失
      doubleTowFlag: item.doubleTowFlag,
      containerNo2: item.containerNo2 ?? '',
      containerWeight2: item.containerWeight2 != null ? String(item.containerWeight2) : '',
    })
    resetAmendmentDialogState()
    setEditingItem(item)   // v2.3：保留 amendment 給 RETURNED 退回原因 banner 顯示
    // resetAmendmentDialogState 把 areaCode 清空，補登 v1.6 後端有 originLocationAreaCode / destinationLocationAreaCode
    // 直接帶入讓 cascade dropdown 能顯示既有起訖地（否則只剩 location code 但縣市空、UI 看起來「請選擇」）
    if (item.originLocationAreaCode) setFromAreaCode(item.originLocationAreaCode)
    if (item.destinationLocationAreaCode) setToAreaCode(item.destinationLocationAreaCode)
    setDialogResyncToken((t) => t + 1)   // v2.3.x：force re-resolve 所有關聯
    setDialogMode('complete')
    // 進入後自動 lookup 船掛 → 取得船名 / vesselInfo（即使委託人脫鉤，船名顯示仍需）
    if (item.vesselRegNo) handleVesselBlur(item.vesselRegNo)
  }

  /** 3. 開 TASK_Q 查詢 Dialog */
  const openTaskQDialog = () => {
    setTaskQContainerInput('')
    setTaskQResults([])
    setTaskQSearched(false)
    setError(null)
    setTaskQDialogOpen(true)
  }

  const searchTaskQ = async () => {
    const cn = taskQContainerInput.trim()
    if (!cn) {
      setError('請輸入貨櫃號碼')
      return
    }
    setTaskQLoading(true)
    setTaskQSearched(false)
    try {
      const data = await missionAmendmentService.findTaskQByContainer(cn)
      setTaskQResults(data)
      setTaskQSearched(true)
    } catch (err) {
      console.error('查 TASK_Q 失敗', err)
      setError(describeApiError(err, '查詢派工資料失敗，請稍後再試'))
      setTaskQResults([])
      setTaskQSearched(true)
    } finally {
      setTaskQLoading(false)
    }
  }

  const pickTaskQ = (item: TaskQItem) => {
    if (!driver) return
    setForm({
      ...EMPTY_FORM,
      sourceTaskQId: item.id,
      trailerCoCd: driver.companyCode,
      // v2.3.x：預設拖運日期 = 當前班別；車號優先 TaskQ 帶來的，沒有才用司機目前車
      dutyDate: currentDutyDate || todayStr(),
      vesselRegNo: item.vesselRegNo ?? '',
      containerNo: item.containerNo ?? '',
      containerSize: (item.containerSize as '20' | '40' | '45') ?? '20',
      emptyLaden: (item.emptyLaden as 'E' | 'L') ?? 'L',
      containerWeight: item.containerWeight != null ? String(item.containerWeight) : '',
      originLocation: item.originLocation ?? '',
      destinationLocation: item.destinationLocation ?? '',
      trailerNo: item.trailerNo ?? driver.currentVehicleNo ?? '',
    })
    resetAmendmentDialogState()
    setTaskQDialogOpen(false)
    setDialogResyncToken((t) => t + 1)   // v2.3.x：force re-resolve 所有關聯
    setDialogMode('taskq')
    // 進入後自動 lookup 船掛
    if (item.vesselRegNo) handleVesselBlur(item.vesselRegNo)
  }

  // ====================================================================
  // 儲存草稿 — 寬鬆 validation，第一次 createDraft、之後 updateDraft
  // ====================================================================

  const saveDraft = async () => {
    if (!driver) return
    // 草稿階段只要選了拖車公司就能存
    const errs: Record<string, string> = {}
    if (form.doubleTowFlag && form.containerNo2 && form.containerNo === form.containerNo2) {
      errs.containerNo2 = '雙拖兩櫃號不可相同'
    }
    setFormErrors(errs)
    if (Object.keys(errs).length > 0) return

    setSubmitting(true)
    try {
      const payload = {
        trailerCoCd: form.trailerCoCd || driver.companyCode,
        // v2.3.x：草稿即可帶 dailyReportId / trailerNo（後端 LineCreateDraftRequest 已支援）
        dailyReportId: form.dailyReportId || null,
        trailerNo: form.trailerNo || '',
        dutyDate: form.dutyDate || null,
        vesselRegNo: form.vesselRegNo,
        amendmentVslName: form.amendmentVslName || null,
        containerNo: form.containerNo,
        containerSize: form.containerSize,
        emptyLaden: form.emptyLaden,
        ioType: 'I' as const,
        containerWeight: null,
        originLocation: form.originLocation,
        destinationLocation: form.destinationLocation,
        billingRequester: form.billingRequester || driver.companyCode,
        doubleTowFlag: form.doubleTowFlag,
        containerNo2: form.doubleTowFlag ? form.containerNo2 : null,
        containerWeight2: null,
      }
      let amendmentId = form.amendmentId
      if (!amendmentId) {
        // 第一次存草稿才會帶 mode + sourceTaskQId；後續 updateDraft 不動 mode
        const draftMode: 'SELF_LINEAT' | 'FROM_TASK_Q' = dialogMode === 'taskq' ? 'FROM_TASK_Q' : 'SELF_LINEAT'
        const created = await missionAmendmentService.createDraft({
          ...payload,
          mode: draftMode,
          sourceTaskQId: draftMode === 'FROM_TASK_Q' ? form.sourceTaskQId ?? null : null,
        })
        amendmentId = created.id
        setForm((f) => ({ ...f, amendmentId }))
        // 新建的草稿加進 list 最前面 — 避免使用者退出 dialog 後看到「該筆不存在」
        setList((prev) => [created, ...prev])
      } else {
        // 拿回最新版 amendment 直接替換 list 內對應 row — 避免再次進入時看到舊資料
        const updated = await missionAmendmentService.updateDraft(amendmentId, payload)
        setList((prev) => prev.map((x) => (x.id === updated.id ? updated : x)))
      }
      setError(null)
      // 提示使用者已儲存
      setSavedHint('草稿已儲存')
      setTimeout(() => setSavedHint(null), 2000)
    } catch (err) {
      console.error('儲存草稿失敗', err)
      const msg = describeApiError(err, '儲存失敗，請稍後再試')
      setError(msg)
      setErrorDialog({ title: '儲存失敗', message: msg }) // 強提示：草稿儲存失敗也跳窗
    } finally {
      setSubmitting(false)
    }
  }

  // ====================================================================
  // 送出前資料合理性檢核（與 WEB 端同 pattern）
  //   - ISO 6346：格式錯（DSP-0706）硬擋；檢核碼不符（DSP-0707）軟警示
  //   - 雙拖時兩櫃號各別檢核
  // ====================================================================
  const validateContainerOrConfirm = async (no: string, no2: string | null = null): Promise<boolean> => {
    const check = async (value: string): Promise<boolean> => {
      try {
        await missionAmendmentService.validateContainerNo(value)
        return true
      } catch (e: unknown) {
        const err = e as { code?: string; message?: string }
        if (err.code === 'DSP-0706') {
          setError(`貨櫃號碼 ${value} 格式錯誤：需為 4 碼英文 + 7 碼數字（共 11 碼）`)
          return false
        }
        if (err.code === 'DSP-0707') {
          return await confirmYesNo('貨櫃號碼檢核碼不符', `${value}：是否仍要繼續儲存？`)
        }
        // 其他例外不擋（網路錯誤等）
        console.warn('validateContainerNo 未預期錯誤，略過硬擋', e)
        return true
      }
    }
    if (no && no.trim()) {
      if (!(await check(no.trim()))) return false
    }
    if (no2 && no2.trim()) {
      if (!(await check(no2.trim()))) return false
    }
    return true
  }

  // ====================================================================
  // 送出 — 完整 validation，先 ensure draft 後 completeAssignedShell
  // ====================================================================

  const submit = async () => {
    if (!driver) return
    const errs: Record<string, string> = {}
    // v2 #5：dailyReportId / dutyDate 至少其一；無打卡時 trailerNo 必填
    if (!form.dailyReportId && !form.dutyDate) errs.dailyReportId = '請選擇打卡紀錄，或指定拖運日期'
    if (!form.dailyReportId && !form.trailerNo) errs.trailerNo = '無打卡紀錄時，車號為必填'
    if (!form.vesselRegNo) errs.vesselRegNo = '船掛不可為空'
    if (!form.billingRequester) errs.billingRequester = '請選擇委託人'
    if (!form.containerNo) errs.containerNo = '櫃號不可為空'
    if (!form.containerSize) errs.containerSize = '請選擇尺寸'
    if (!form.emptyLaden) errs.emptyLaden = '請選擇空重'
    if (!form.originLocation) errs.originLocation = '起點不可為空'
    if (!form.destinationLocation) errs.destinationLocation = '迄點不可為空'
    if (form.doubleTowFlag) {
      if (form.containerSize !== '20') errs.containerSize = '雙拖補登僅允許 20 呎櫃'
      if (!form.containerNo2) errs.containerNo2 = '雙拖第二櫃號不可為空'
      else if (form.containerNo === form.containerNo2) errs.containerNo2 = '雙拖兩櫃號不可相同'
    }
    setFormErrors(errs)
    if (Object.keys(errs).length > 0) return

    // ISO 6346 櫃號檢核
    const containerOk = await validateContainerOrConfirm(
      form.containerNo,
      form.doubleTowFlag ? form.containerNo2 : null,
    )
    if (!containerOk) return

    // v2 #5：無打卡時跳明確確認
    if (!form.dailyReportId && form.dutyDate) {
      const ok = await confirmYesNo(
        '無對應打卡紀錄',
        `將以拖運日期 ${form.dutyDate} 與車號 ${form.trailerNo || '-'} 補登；司機當天並未打卡，是否確認送出？`,
      )
      if (!ok) return
    }

    setSubmitting(true)
    try {
      if (form.amendmentId) {
        // 已有草稿（complete 或從 list 點 PENDING_FILL 進來）
        // 先 updateDraft 把最新欄位寫進去，再 completeAssignedShell 送出
        await missionAmendmentService.updateDraft(form.amendmentId, {
          trailerCoCd: form.trailerCoCd || driver.companyCode,
          dailyReportId: form.dailyReportId || null,
          trailerNo: form.trailerNo || '',
          dutyDate: form.dutyDate || null,
          vesselRegNo: form.vesselRegNo,
          amendmentVslName: form.amendmentVslName || null,
          containerNo: form.containerNo,
          containerSize: form.containerSize,
          emptyLaden: form.emptyLaden,
          ioType: 'I',
          containerWeight: null,
          originLocation: form.originLocation,
          destinationLocation: form.destinationLocation,
          billingRequester: form.billingRequester || driver.companyCode,
          doubleTowFlag: form.doubleTowFlag,
          containerNo2: form.doubleTowFlag ? form.containerNo2 : null,
          containerWeight2: null,
        })
        // v2.3：RETURNED 走 resubmit（已 updateContent，只需轉狀態）；
        //      PENDING_FILL 走 completeAssignedShell（同舊邏輯）
        if (editingItem?.status === 'RETURNED') {
          await missionAmendmentService.resubmit(form.amendmentId)
        } else {
          await missionAmendmentService.completeAssignedShell(form.amendmentId, {
            dailyReportId: form.dailyReportId || null,
            trailerNo: form.trailerNo,
            dutyDate: form.dutyDate || null,
            containerWeight: null,
            doubleTowFlag: form.doubleTowFlag,
            containerNo2: form.doubleTowFlag ? form.containerNo2 : null,
            containerWeight2: null,
          })
        }
      } else {
        const mode: AmendmentMode = dialogMode === 'taskq' ? 'FROM_TASK_Q' : 'SELF_LINEAT'
        await missionAmendmentService.createOneShot({
          mode,
          driverId: driver.driverId,
          dailyReportId: form.dailyReportId || null,
          trailerCoCd: form.trailerCoCd,
          trailerNo: form.trailerNo,
          dutyDate: form.dutyDate || null,
          vesselRegNo: form.vesselRegNo,
          amendmentVslName: form.amendmentVslName || null,
          containerNo: form.containerNo,
          containerSize: form.containerSize,
          emptyLaden: form.emptyLaden,
          // v1 進出口別固定進口；前端不顯示，欄位保留供未來開放（與後端對齊）
          ioType: 'I',
          containerWeight: null,
          originLocation: form.originLocation,
          destinationLocation: form.destinationLocation,
          billingRequester: form.billingRequester || driver.companyCode,
          doubleTowFlag: form.doubleTowFlag,
          containerNo2: form.doubleTowFlag ? form.containerNo2 : null,
          containerWeight2: null,
          sourceTaskQId: form.sourceTaskQId,
        })
      }
      setDialogMode(null)
      await loadList(driver.driverId)
    } catch (err) {
      console.error('送出失敗', err)
      const msg = describeApiError(err, '送出失敗，請稍後再試')
      setError(msg)
      setErrorDialog({ title: '送出失敗', message: msg }) // 強提示：跳窗，避免停在底部送出鈕看不到頂端 Alert
    } finally {
      setSubmitting(false)
    }
  }

  // 表單內掃描成功 — 拆船掛 + 櫃號填入 form
  //   規則：barcode 前 6 碼數字當船掛，後續英數字當櫃號（ISO 6346：4 字母 + 7 數字）
  //   情境 a) raw="999999ABCD1234567"：船掛 999999、櫃號 ABCD1234567
  //   情境 b) raw="ABCD1234567"（只有櫃號）：船掛保留不動、櫃號填入
  const handleFormScanSuccess = (decodedText: string, rawText?: string) => {
    setFormScannerOpen(false)
    const text = (rawText ?? decodedText).toUpperCase()
    const fullMatch = text.match(/^(\d{6})([A-Z]{4}\d{7})$/)
    if (fullMatch) {
      const vsl = fullMatch[1]
      const cnt = fullMatch[2]
      setForm((f) => ({ ...f, vesselRegNo: vsl, containerNo: cnt }))
      // 順便觸發船掛 lookup → 委託人 dropdown 自動帶
      void handleVesselBlur(vsl)
      return
    }
    // 沒前 6 碼船掛 → 只填櫃號
    const cn = decodedText.toUpperCase()
    if (/^[A-Z]{4}\d{7}$/.test(cn)) {
      setForm((f) => ({ ...f, containerNo: cn }))
    } else {
      // 完全無法解析 → 至少把原文塞進櫃號讓使用者編輯
      setForm((f) => ({ ...f, containerNo: cn }))
    }
  }

  // 條碼掃描成功 — 拆船掛 + 貨櫃號，填入查詢欄位並自動搜尋
  // 假設：barcode 內若同時含船掛與貨櫃號碼，船掛為 ISO 11 碼前的 6 碼數字
  //   範例 raw text: "999999ABCD1234567" → 船掛 999999 + 貨櫃 ABCD1234567
  // ⚠️ 船掛位置與長度仍待客戶端確認，先依此假設實作
  const handleScanSuccess = async (decodedText: string, rawText?: string) => {
    const cn = decodedText.toUpperCase().slice(0, 11)
    setScannerOpen(false)
    setTaskQContainerInput(cn)

    // 從 raw text 嘗試擷取船掛（ISO 前的 6 碼數字）
    if (rawText) {
      const m = rawText.match(/(\d{6})[A-Z]{4}\d{7}/)
      if (m && m[1]) {
        // 暫存到 form.vesselRegNo，後續 pickTaskQ 走另一條 path 會覆蓋；
        // 若找不到 task_q 時，使用者切到自發補登入口仍會看到此值
        setForm((f) => ({ ...f, vesselRegNo: m[1] }))
      }
    }

    setTaskQLoading(true)
    try {
      const data = await missionAmendmentService.findTaskQByContainer(cn)
      setTaskQResults(data)
    } catch (err) {
      console.error('掃描後查 TASK_Q 失敗', err)
    } finally {
      setTaskQLoading(false)
    }
  }

  // ====================================================================
  // Render
  // ====================================================================

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
      }}>
        <CircularProgress size={60} sx={{ color: '#6B705C' }} />
        <Typography variant="body1" sx={{ mt: 2, color: '#6B705C' }}>載入中...</Typography>
      </Box>
    )
  }

  return (
    <Box sx={{
      minHeight: '100vh',
      background: 'linear-gradient(135deg, #FFE8D6 0%, #DDBEA9 100%)',
    }}>
      <AppBar position="static" sx={{ backgroundColor: '#6B705C' }} elevation={2}>
        <Toolbar>
          <IconButton edge="start" color="inherit" onClick={() => navigate('/home')}>
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>任務補登</Typography>
          <IconButton color="inherit" onClick={() => driver && loadList(driver.driverId)}>
            <RefreshIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <Container maxWidth="sm" sx={{ py: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* 入口按鈕 — 同一行左右並排 */}
        <Stack direction="row" spacing={1.5} sx={{ mb: 2 }}>
          <Button
            fullWidth
            variant="contained"
            startIcon={<AddCircleOutlineIcon />}
            size="large"
            onClick={startSelfAmendment}
            sx={{
              backgroundColor: '#6B705C',
              color: 'white',
              py: 1.5,
              fontSize: '1rem',
              fontWeight: 700,
              borderRadius: 3,
              boxShadow: '0 4px 12px rgba(107, 112, 92, 0.3)',
              '&:hover': {
                backgroundColor: '#5A5F4A',
                boxShadow: '0 6px 16px rgba(107, 112, 92, 0.4)',
              },
            }}
          >
            手動補登
          </Button>
          <Button
            fullWidth
            variant="outlined"
            startIcon={<QrCodeScannerIcon />}
            size="large"
            onClick={openTaskQDialog}
            sx={{
              borderColor: '#A5A58D',
              color: '#A5A58D',
              py: 1.5,
              fontSize: '0.9rem',
              fontWeight: 600,
              borderRadius: 3,
              lineHeight: 1.2,
              '&:hover': {
                borderColor: '#8F8F75',
                backgroundColor: 'rgba(165, 165, 141, 0.08)',
              },
            }}
          >
            掃描條碼補登
          </Button>
        </Stack>

        <Divider sx={{ my: 2 }}><Typography variant="caption">我的補登</Typography></Divider>

        {/* 列表 — 未完成 / 已完成 分組，Switch 控制是否顯示已完成 */}
        {(() => {
          const STATUS_ORDER: Record<AmendmentStatus, number> = {
            PENDING_FILL: 0,
            RETURNED: 1,    // v2.3 退回重填 — 需司機修改後重送，與待填寫並列前段
            PENDING_REVIEW: 2,
            APPROVED: 3,
            REJECTED: 4,
          }
          // v2.3：RETURNED **不算**已完成（需重填重送）— 與 PENDING_FILL 並列為未完成
          const isCompleted = (s: AmendmentStatus) => s === 'APPROVED' || s === 'REJECTED'
          // 排序：狀態 (未完成優先) → createdAt DESC
          const sorted = [...list].sort((a, b) => {
            const da = STATUS_ORDER[a.status as AmendmentStatus] - STATUS_ORDER[b.status as AmendmentStatus]
            if (da !== 0) return da
            return new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
          })
          const incompleteList = sorted.filter((x) => !isCompleted(x.status as AmendmentStatus))
          const completedList = sorted.filter((x) => isCompleted(x.status as AmendmentStatus))

          const renderItem = (item: MissionAmendment) => {
            const status = item.status as AmendmentStatus
            // v2.3：RETURNED 跟 PENDING_FILL 一樣，可被司機進入「補完」流程編輯 → 編輯送出後自動回 PENDING_REVIEW
            const interactable = status === 'PENDING_FILL' || status === 'RETURNED'
            return (
              <ListItem key={item.id} disablePadding divider>
                <ListItemButton
                  onClick={() => interactable ? startCompleteAssigned(item) : setViewingAmendment(item)}
                >
                  <ListItemText
                    primaryTypographyProps={{ component: 'div' }}
                    secondaryTypographyProps={{ component: 'div' }}
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                        <Typography variant="body2" fontWeight="bold" sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, lineHeight: 1.2 }}>
                          {item.vesselRegNo} / {item.doubleTowFlag && item.containerNo2 ? <NumChip n={1} /> : null}{item.containerNo}
                          {item.doubleTowFlag && item.containerNo2 && (
                            <>
                              <Box component="span" sx={{ mx: 0.25 }} /><NumChip n={2} />{item.containerNo2}
                            </>
                          )}
                        </Typography>
                        <Chip
                          size="small"
                          label={AMENDMENT_STATUS_LABEL[status]}
                          sx={{ ...STATUS_CHIP_SX[status], fontWeight: 600 }}
                        />
                        {/* v2.3：RETURNED / REJECTED 顯示「查看原因」icon button，點下開 viewingAmendment dialog 看 rejectReason */}
                        {(status === 'RETURNED' || status === 'REJECTED') && item.rejectReason && (
                          <IconButton
                            size="small"
                            onClick={(e) => {
                              e.stopPropagation()
                              setViewingAmendment(item)
                            }}
                            sx={{
                              p: 0.25,
                              color: status === 'RETURNED' ? '#F4A261' : '#d32f2f',
                            }}
                            aria-label={status === 'RETURNED' ? '查看退回原因' : '查看退件原因'}
                          >
                            <InfoOutlinedIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    }
                    secondary={
                      <>
                        <Typography variant="caption" component="span" display="block">
                          {AMENDMENT_MODE_LABEL[item.amendmentMode]} · {item.originLocationShortName || item.originLocation} → {item.destinationLocationShortName || item.destinationLocation}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {new Date(item.createdAt).toLocaleString('zh-TW')}
                        </Typography>
                      </>
                    }
                  />
                </ListItemButton>
              </ListItem>
            )
          }

          // 完全沒紀錄
          if (sorted.length === 0) {
            return (
              <Paper sx={{ p: 3, textAlign: 'center', color: 'text.secondary', borderRadius: 3 }} elevation={2}>
                目前沒有補登紀錄
              </Paper>
            )
          }

          return (
            <>
              <List sx={{
                bgcolor: 'background.paper',
                borderRadius: 3,
                boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                overflow: 'hidden',
                p: 0,
              }}>
                <ListSubheader sx={{
                  bgcolor: '#FBF7F0', color: '#6B705C',
                  fontWeight: 700, lineHeight: '32px',
                  borderBottom: '1px solid #eee',
                }}>
                  未完成 ({incompleteList.length})
                </ListSubheader>
                {incompleteList.length === 0 ? (
                  <ListItem>
                    <ListItemText
                      primaryTypographyProps={{ variant: 'body2', color: 'text.secondary', sx: { textAlign: 'center' } }}
                      primary="目前沒有待處理的補登"
                    />
                  </ListItem>
                ) : (
                  incompleteList.map(renderItem)
                )}
              </List>

              {/* 切換顯示已完成 */}
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mt: 2, px: 1 }}>
                <Typography variant="caption" color="text.secondary">
                  已完成 ({completedList.length})
                </Typography>
                <FormControlLabel
                  control={
                    <Switch
                      size="small"
                      checked={showCompleted}
                      onChange={(e) => setShowCompleted(e.target.checked)}
                    />
                  }
                  label={
                    <Typography variant="caption">
                      顯示已完成
                    </Typography>
                  }
                  sx={{ m: 0 }}
                />
              </Box>

              {showCompleted && completedList.length > 0 && (
                <List sx={{
                  bgcolor: 'background.paper',
                  borderRadius: 3,
                  boxShadow: '0 2px 8px rgba(0,0,0,0.05)',
                  overflow: 'hidden',
                  mt: 1,
                  p: 0,
                }}>
                  <ListSubheader sx={{
                    bgcolor: '#F4F4F0', color: '#888',
                    fontWeight: 700, lineHeight: '32px',
                    borderBottom: '1px solid #eee',
                  }}>
                    已完成 ({completedList.length})
                  </ListSubheader>
                  {completedList.map(renderItem)}
                </List>
              )}
            </>
          )
        })()}

        {/* 補登表單 Dialog (self / complete / taskq 共用) — fullScreen 整頁體驗 */}
        <Dialog
          open={dialogMode !== null}
          onClose={() => setDialogMode(null)}
          fullScreen
          TransitionProps={{ timeout: 200 }}
        >
          {/* 上方 AppBar 與其他 LINE@ page 一致風格（棕色底 + 返回鍵 + 標題） */}
          <AppBar position="sticky" sx={{ backgroundColor: '#6B705C' }} elevation={2}>
            <Toolbar>
              <IconButton edge="start" color="inherit" onClick={() => setDialogMode(null)} aria-label="返回">
                <ArrowBackIcon />
              </IconButton>
              <EditNoteIcon sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
                {dialogMode === 'complete' ? '完成被指派的補登' :
                 dialogMode === 'taskq' ? '從掃描條碼補登' : '手動補登'}
              </Typography>
              <Button onClick={saveDraft} disabled={submitting} sx={{ color: 'white', fontWeight: 600 }}>
                {submitting ? '...' : '儲存草稿'}
              </Button>
            </Toolbar>
          </AppBar>
          <DialogContent ref={formDialogContentRef} sx={{ backgroundColor: '#fafafa', p: 2 }}>
            <Stack spacing={2} sx={{ mt: 1 }}>
              {/* 送出/儲存失敗即時顯示於對話框內 — 避免使用者退出後才在列表頁看到、誤以為沒反應 */}
              {error && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              {/* v2.3：RETURNED 退回原因 banner — 司機修改前先看到主管要求 */}
              {editingItem?.status === 'RETURNED' && editingItem.rejectReason && (
                <Alert
                  severity="warning"
                  icon={<InfoOutlinedIcon />}
                  sx={{
                    backgroundColor: '#FFF4E6',
                    borderLeft: '4px solid #F4A261',
                    color: '#6B4423',
                    '& .MuiAlert-icon': { color: '#F4A261' },
                  }}
                >
                  <Typography variant="caption" sx={{ display: 'block', fontWeight: 600, color: '#666' }}>
                    退回原因（請依此修改後重送）
                  </Typography>
                  <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', color: '#F4A261', mt: 0.5 }}>
                    {editingItem.rejectReason}
                  </Typography>
                </Alert>
              )}

              {/* ════════ 區塊 1：派車資料 ════════ */}
              {/*
                v2.3.x：
                - 移除「打卡紀錄」TextField（不再要求司機先選打卡）
                - 拖運日期改 selector（自己選 / 從過往打卡選），預設帶當前班別日期
                - 車號改 MobilePicker（司機所屬公司全部車輛），預設帶當前班別車輛
                - dailyReportId 仍會在「從過往打卡選」時被一同帶入；手選日期則為 null
              */}
              <SectionCard title="派車資料" icon={<LocalShippingIcon fontSize="small" />}>
                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    {/* 拖運日期 — readonly TextField + 點擊開 selector */}
                    <TextField
                      label="拖運日期"
                      required
                      value={form.dutyDate}
                      placeholder="點此選擇"
                      InputLabelProps={{ shrink: true }}
                      onClick={() => setDutyDateSelectorOpen(true)}
                      error={!!formErrors.dailyReportId || !!formErrors.dutyDate}
                      helperText={
                        formErrors.dailyReportId
                        || formErrors.dutyDate
                        || (form.dailyReportId ? '已連結至打卡紀錄' : (form.dutyDate ? '無對應打卡' : ''))
                      }
                      FormHelperTextProps={{ sx: form.dailyReportId ? undefined : { color: '#B45309', whiteSpace: 'nowrap' } }}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <EditNoteIcon sx={{ color: '#6B705C' }} />
                          </InputAdornment>
                        ),
                        sx: { cursor: 'pointer', '& input': { cursor: 'pointer' } },
                      }}
                      fullWidth
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    {/* 車號 — MobilePicker 從司機所屬公司所有車輛中選 */}
                    <TextField
                      label="車號"
                      required
                      value={form.trailerNo}
                      placeholder="點此選擇"
                      InputLabelProps={{ shrink: true }}
                      onClick={() => setVehiclePickerOpen(true)}
                      error={!!formErrors.trailerNo}
                      helperText={formErrors.trailerNo}
                      InputProps={{
                        readOnly: true,
                        endAdornment: (
                          <InputAdornment position="end">
                            <EditNoteIcon sx={{ color: '#6B705C' }} />
                          </InputAdornment>
                        ),
                        sx: { cursor: 'pointer', '& input': { cursor: 'pointer' } },
                      }}
                      fullWidth
                    />
                  </Box>
                </Stack>
              </SectionCard>

              {/* ════════ 區塊 2：船舶資料 ════════ */}
              <SectionCard title="船舶資料" icon={<DirectionsBoatIcon fontSize="small" />}>
                {/* 掃描條碼一鍵帶入船掛 + 櫃號 */}
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<CameraAltIcon />}
                  onClick={() => setFormScannerOpen(true)}
                  sx={{
                    borderColor: '#A5A58D',
                    color: '#6B705C',
                    py: 1.25,
                    fontSize: '0.95rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    '&:hover': { borderColor: '#8F8F75', backgroundColor: 'rgba(165,165,141,0.08)' },
                  }}
                >
                  掃描條碼帶入船掛 / 櫃號
                </Button>

                {/* 船掛 + 委託人 同一行 */}
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <TextField
                    label="船掛"
                    required
                    value={form.vesselRegNo}
                    onChange={(e) => {
                      // v2.3：船掛變動 → 清空 amendmentVslName（避免殘留上一輪填的）
                      setForm((f) => ({ ...f, vesselRegNo: e.target.value, amendmentVslName: '' }))
                    }}
                    onBlur={(e) => handleVesselBlur(e.target.value)}
                    inputProps={{ maxLength: 6 }}
                    placeholder="請輸入 6 碼"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.vesselRegNo}
                    helperText={formErrors.vesselRegNo}
                    fullWidth
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  {/*
                    v2.3.x：委託人欄位 — 點 input 或右側筆 icon 都開「選擇方式」dialog
                    使用者再從 dialog 內選「📋 從清單選」/「✍ 手動輸入」
                  */}
                  <TextField
                    label="委託人"
                    required
                    value={
                      consignorOptions.find((o) => o.value === form.billingRequester)?.label
                      ?? form.billingRequester
                    }
                    placeholder="點此選擇或輸入"
                    error={!!formErrors.billingRequester}
                    helperText={formErrors.billingRequester}
                    InputLabelProps={{ shrink: true }}
                    onClick={() => setConsignorSelectorOpen(true)}
                    InputProps={{
                      readOnly: true,
                      endAdornment: (
                        <InputAdornment position="end">
                          <EditNoteIcon sx={{ color: '#6B705C' }} />
                        </InputAdornment>
                      ),
                      sx: {
                        cursor: 'pointer',
                        '& input': { cursor: 'pointer' },
                      },
                    }}
                    fullWidth
                  />
                </Box>
              </Stack>

                {/* v2.3：補登用船名 — 船掛空值 → disabled；船掛有值且 vessel_work 命中 → readonly；否則開放手填 */}
                {(() => {
                  const vesselRegNoEmpty = !(form.vesselRegNo || '').trim()
                  const isDisabled = vesselRegNoEmpty || !!vesselInfo
                  return (
                    <TextField
                      label="船名"
                      value={vesselInfo?.vslName ?? form.amendmentVslName}
                      onChange={(e) => {
                        if (isDisabled) return
                        setForm((f) => ({ ...f, amendmentVslName: e.target.value.slice(0, 60) }))
                      }}
                      placeholder={vesselRegNoEmpty ? '請先輸入船掛' : (vesselInfo ? '' : '船掛若不在系統內，可手填船名')}
                      inputProps={{ maxLength: 60, readOnly: isDisabled }}
                      InputLabelProps={{ shrink: true }}
                      sx={isDisabled ? { '& .MuiInputBase-root': { backgroundColor: '#f5f5f0' } } : undefined}
                      fullWidth
                    />
                  )
                })()}
              </SectionCard>

              {/* ════════ 區塊 3：貨櫃資料 ════════ */}
              <SectionCard title="貨櫃資料" icon={<Inventory2Icon fontSize="small" />}>
                {/* 櫃號 */}
                <Stack direction="row" spacing={2}>
                  <Box sx={{ flex: 1 }}>
                    <TextField
                      label="櫃號"
                    required
                    value={form.containerNo}
                    inputProps={{ maxLength: 11 }}
                    onChange={(e) => {
                      setForm((f) => ({ ...f, containerNo: e.target.value.toUpperCase() }))
                      setContainerNoHint(null)
                    }}
                    onBlur={async (e) => {
                      setContainerNoHint(await computeContainerNoHint(e.target.value.toUpperCase()))
                    }}
                    placeholder="請輸入"
                    InputLabelProps={{ shrink: true }}
                    error={!!formErrors.containerNo}
                    helperText={formErrors.containerNo}
                    fullWidth
                  />
                  {containerNoHint && (
                    <Typography variant="caption" sx={{ color: HINT_COLOR[containerNoHint.level], mt: 0.5, display: 'block' }}>
                      {containerNoHint.text}
                    </Typography>
                  )}
                </Box>
              </Stack>

              {/* 尺寸 + 空重 同一行（預設「請選擇」） */}
              <Stack direction="row" spacing={2}>
                <Box sx={{ flex: 1 }}>
                  <PickerField
                    label="尺寸"
                    required
                    value={form.containerSize}
                    options={[
                      { value: '20', label: '20 呎' },
                      { value: '40', label: '40 呎' },
                      { value: '45', label: '45 呎' },
                    ]}
                    onChange={(v) => {
                      const next = v as '20' | '40' | '45'
                      setForm((f) => ({
                        ...f,
                        containerSize: next,
                        // 切離 20 呎自動關閉雙拖（對齊 WEB）
                        doubleTowFlag: next === '20' ? f.doubleTowFlag : false,
                        containerNo2: next === '20' ? f.containerNo2 : '',
                        containerWeight2: next === '20' ? f.containerWeight2 : '',
                      }))
                    }}
                    error={!!formErrors.containerSize}
                    helperText={formErrors.containerSize}
                  />
                </Box>
                <Box sx={{ flex: 1 }}>
                  <PickerField
                    label="空重"
                    required
                    value={form.emptyLaden}
                    options={[
                      { value: 'E', label: 'E 空櫃' },
                      { value: 'L', label: 'L 重櫃' },
                    ]}
                    onChange={(v) => setForm((f) => ({ ...f, emptyLaden: v as 'E' | 'L' }))}
                    error={!!formErrors.emptyLaden}
                    helperText={formErrors.emptyLaden}
                  />
                </Box>
              </Stack>

              {/* 雙拖區段 — 僅 20 呎櫃顯示（對齊 WEB）；勾選時上下都有分隔線把區塊框出來 */}
              {form.containerSize === '20' && (
                <Box
                  sx={{
                    borderTop: '1px solid #eee',
                    borderBottom: form.doubleTowFlag ? '1px solid #eee' : 'none',
                    pt: 1,
                    pb: form.doubleTowFlag ? 2 : 0,
                  }}
                >
                  <FormControlLabel
                    control={
                      <Checkbox
                        checked={form.doubleTowFlag}
                        onChange={(e) => {
                          const checked = e.target.checked
                          setForm((f) => ({
                            ...f,
                            doubleTowFlag: checked,
                            containerNo2: checked ? f.containerNo2 : '',
                            containerWeight2: checked ? f.containerWeight2 : '',
                          }))
                        }}
                      />
                    }
                    label="雙拖（雙櫃同車）"
                  />
                  {form.doubleTowFlag && (
                    <Box sx={{ mt: 1 }}>
                      <TextField
                        label="第二櫃號"
                        required
                        value={form.containerNo2}
                        inputProps={{ maxLength: 11 }}
                        onChange={(e) => {
                          setForm((f) => ({ ...f, containerNo2: e.target.value.toUpperCase() }))
                          setContainerNo2Hint(null)
                        }}
                        onBlur={async (e) => {
                          setContainerNo2Hint(await computeContainerNoHint(e.target.value.toUpperCase()))
                        }}
                        placeholder="請輸入"
                        InputLabelProps={{ shrink: true }}
                        error={!!formErrors.containerNo2}
                        helperText={formErrors.containerNo2}
                        fullWidth
                      />
                      {containerNo2Hint && (
                        <Typography variant="caption" sx={{ color: HINT_COLOR[containerNo2Hint.level], mt: 0.5, display: 'block' }}>
                          {containerNo2Hint.text}
                        </Typography>
                      )}
                    </Box>
                  )}
                </Box>
              )}

              </SectionCard>

              {/* ════════ 區塊 4：起訖地點 ════════ */}
              <SectionCard title="起訖地點" icon={<PlaceIcon fontSize="small" />}>
                {/* 起點縣市 + 起點 cascade */}
                <Stack direction="row" spacing={1}>
                  <Box sx={{ flex: 1 }}>
                    <PickerField
                      label="起點縣市"
                      required
                      value={fromAreaCode}
                      options={areaCodeOptions.map((o) => ({ value: o.code, label: o.name }))}
                      onChange={(v) => {
                        setFromAreaCode(v)
                        setForm((f) => ({ ...f, originLocation: '' }))
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <PickerField
                      label="起點"
                      required
                      disabled={!fromAreaCode}
                      value={form.originLocation}
                      options={fromLocationOptions.map((o) => ({ value: o.value, label: o.shortName || o.label, subLabel: o.shortName ? o.label : undefined }))}
                      onChange={(v) => setForm((f) => ({ ...f, originLocation: v }))}
                      error={!!formErrors.originLocation}
                      helperText={formErrors.originLocation}
                    />
                  </Box>
                </Stack>
                {/* 迄點縣市 + 迄點 cascade */}
                <Stack direction="row" spacing={1}>
                  <Box sx={{ flex: 1 }}>
                    <PickerField
                      label="迄點縣市"
                      required
                      value={toAreaCode}
                      options={areaCodeOptions.map((o) => ({ value: o.code, label: o.name }))}
                      onChange={(v) => {
                        setToAreaCode(v)
                        setForm((f) => ({ ...f, destinationLocation: '' }))
                      }}
                    />
                  </Box>
                  <Box sx={{ flex: 1 }}>
                    <PickerField
                      label="迄點"
                      required
                      disabled={!toAreaCode}
                      value={form.destinationLocation}
                      options={toLocationOptions.map((o) => ({ value: o.value, label: o.shortName || o.label, subLabel: o.shortName ? o.label : undefined }))}
                      onChange={(v) => setForm((f) => ({ ...f, destinationLocation: v }))}
                      error={!!formErrors.destinationLocation}
                      helperText={formErrors.destinationLocation}
                    />
                  </Box>
                </Stack>
              </SectionCard>
            </Stack>
          </DialogContent>
          {savedHint && (
            <Box sx={{
              position: 'sticky', bottom: 80, left: 0, right: 0,
              textAlign: 'center', color: '#198754', py: 1, fontSize: '0.85rem',
              backgroundColor: '#e8f5e9', borderTop: '1px solid #c8e6c9',
            }}>
              {savedHint}
            </Box>
          )}
          {/* 底部固定送出按鈕（大按鈕，方便司機戴手套點按） */}
          <Box sx={{
            position: 'sticky', bottom: 0, left: 0, right: 0,
            backgroundColor: 'white',
            borderTop: '1px solid #e0e0e0',
            px: 2,
            pt: 1.5,
            pb: 'calc(12px + env(safe-area-inset-bottom))',  // iOS Home Indicator 安全距離
            display: 'flex', gap: 1,
          }}>
            <Button
              variant="contained"
              fullWidth
              size="large"
              onClick={submit}
              disabled={submitting}
              sx={{ backgroundColor: '#6B705C', '&:hover': { backgroundColor: '#5a5e4d' }, py: 1.25, fontSize: '1rem', borderRadius: 1.5 }}
            >
              {submitting ? '處理中…' : '送出'}
            </Button>
          </Box>
        </Dialog>

        {/* 補登詳情 Dialog — 非 PENDING_FILL 點下就開這個 */}
        <Dialog
          open={!!viewingAmendment}
          onClose={() => setViewingAmendment(null)}
          fullScreen
          TransitionProps={{ timeout: 200 }}
        >
          <AppBar position="sticky" sx={{ backgroundColor: '#6B705C' }} elevation={2}>
            <Toolbar>
              <IconButton edge="start" color="inherit" onClick={() => setViewingAmendment(null)} aria-label="返回">
                <ArrowBackIcon />
              </IconButton>
              <EditNoteIcon sx={{ mr: 1 }} />
              <Typography variant="h6" sx={{ flex: 1, fontWeight: 700 }}>
                補登詳情
              </Typography>
              {viewingAmendment && (
                <Chip
                  size="small"
                  label={AMENDMENT_STATUS_LABEL[viewingAmendment.status as AmendmentStatus]}
                  sx={{
                    ...STATUS_CHIP_SX[viewingAmendment.status as AmendmentStatus],
                    fontWeight: 600,
                  }}
                />
              )}
            </Toolbar>
          </AppBar>
          <DialogContent sx={{ backgroundColor: '#fafafa', p: 2 }}>
            {viewingAmendment && (
              <Stack spacing={1.5} sx={{ mt: 1 }}>
                <DetailRow label="模式" value={AMENDMENT_MODE_LABEL[viewingAmendment.amendmentMode]} />
                <DetailRow label="車號" value={viewingAmendment.trailerNo ?? '-'} />
                <Divider />
                <DetailRow label="船掛" value={viewingAmendment.vesselRegNo || '-'} />
                <DetailRow
                  label="委託人"
                  value={viewingAmendment.billingRequesterName
                    ? `${viewingAmendment.billingRequester} - ${viewingAmendment.billingRequesterName}`
                    : (viewingAmendment.billingRequester || '-')}
                />
                <DetailRow label="進出口" value={formatIoType(viewingAmendment.ioType)} />
                <Divider />
                <DetailRow label="櫃號" value={viewingAmendment.containerNo || '-'} />
                <DetailRow label="櫃重 (噸)" value={viewingAmendment.containerWeight ?? '-'} />
                <DetailRow label="尺寸" value={formatContainerSize(viewingAmendment.containerSize)} />
                <DetailRow label="空重" value={formatEmptyLaden(viewingAmendment.emptyLaden)} />
                {viewingAmendment.doubleTowFlag && (
                  <>
                    <Divider />
                    <DetailRow label="雙拖" value="是" />
                    <DetailRow label="第二櫃號" value={viewingAmendment.containerNo2 || '-'} />
                    <DetailRow label="第二櫃重 (噸)" value={viewingAmendment.containerWeight2 ?? '-'} />
                  </>
                )}
                <Divider />
                <DetailRow label="起點" value={viewingAmendment.originLocationShortName || viewingAmendment.originLocation || '-'} />
                <DetailRow label="迄點" value={viewingAmendment.destinationLocationShortName || viewingAmendment.destinationLocation || '-'} />
                <Divider />
                <DetailRow label="建立時間" value={new Date(viewingAmendment.createdAt).toLocaleString('zh-TW')} />
                {viewingAmendment.reviewedAt && (
                  <DetailRow label="審核時間" value={new Date(viewingAmendment.reviewedAt).toLocaleString('zh-TW')} />
                )}
                {viewingAmendment.rejectReason && (() => {
                  // v2.3：依 status 切換 label 與配色 — RETURNED 用橘色強調「請修改後重送」
                  const isReturned = viewingAmendment.status === 'RETURNED'
                  const accent = isReturned ? '#F4A261' : '#d32f2f'
                  const bg = isReturned ? '#FFF4E6' : '#fdecea'
                  return (
                    <>
                      <Divider />
                      <Box sx={{ mt: 1, p: 1.5, backgroundColor: bg, borderRadius: 1, borderLeft: `4px solid ${accent}` }}>
                        <Typography variant="caption" sx={{ color: '#666', display: 'block', mb: 0.5 }}>
                          {isReturned ? '退回原因（請依此修改後重送）' : '退件原因'}
                        </Typography>
                        <Typography variant="body2" sx={{ color: accent, whiteSpace: 'pre-wrap' }}>
                          {viewingAmendment.rejectReason}
                        </Typography>
                      </Box>
                    </>
                  )
                })()}
              </Stack>
            )}
          </DialogContent>
          <Box sx={{
            position: 'sticky', bottom: 0, left: 0, right: 0,
            backgroundColor: 'white',
            borderTop: '1px solid #e0e0e0',
            px: 2, pt: 1.5,
            pb: 'calc(12px + env(safe-area-inset-bottom))',
            display: 'flex', gap: 1,
          }}>
            {viewingAmendment?.status === 'REJECTED' && (
              <Button
                variant="contained"
                fullWidth
                size="large"
                onClick={async () => {
                  if (!viewingAmendment) return
                  try {
                    await missionAmendmentService.reopen(viewingAmendment.id)
                    setViewingAmendment(null)
                    if (driver) await loadList(driver.driverId)
                  } catch (err) {
                    setError(`重新打開失敗：${String(err)}`)
                  }
                }}
                sx={{ backgroundColor: '#6B705C', py: 1.25, fontSize: '1rem', borderRadius: 1.5 }}
              >
                重新打開編輯
              </Button>
            )}
            <Button
              variant="outlined"
              fullWidth
              size="large"
              onClick={() => setViewingAmendment(null)}
              sx={{ borderColor: '#6B705C', color: '#6B705C', py: 1.25, fontSize: '1rem', borderRadius: 1.5 }}
            >
              關閉
            </Button>
          </Box>
        </Dialog>

        {/* 打卡選擇 sub-Dialog */}
        <Dialog
          open={pickerOpen}
          onClose={() => setPickerOpen(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 700,
            borderBottom: '1px solid #e0e0e0',
            color: '#6B705C',
          }}>
            <SearchIcon sx={{ color: '#6B705C' }} />
            選擇打卡紀錄
          </DialogTitle>
          <DialogContent>
            {pickerLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress />
              </Box>
            ) : pickerResults.length === 0 ? (
              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                沒有打卡紀錄
              </Typography>
            ) : (
              <List dense>
                {pickerResults.map((item) => (
                  <ListItemButton key={item.dailyReportId} onClick={() => pickDailyReport(item)}>
                    <ListItemText
                      primary={`${item.vehicleNo} · ${item.reportDate}`}
                      secondary={
                        <>
                          {item.clockInTime ? new Date(item.clockInTime).toLocaleString('zh-TW') : '-'}
                          {' · '}
                          {item.reportStatus === 'O' ? '未交班' : '已交班'}
                        </>
                      }
                    />
                  </ListItemButton>
                ))}
              </List>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setPickerOpen(false)}>關閉</Button>
          </DialogActions>
        </Dialog>

        {/* TASK_Q 查詢 Dialog */}
        <Dialog
          open={taskQDialogOpen}
          onClose={() => setTaskQDialogOpen(false)}
          fullWidth
          maxWidth="xs"
          PaperProps={{ sx: { borderRadius: 3 } }}
        >
          <DialogTitle sx={{
            display: 'flex',
            alignItems: 'center',
            gap: 1,
            fontWeight: 700,
            borderBottom: '1px solid #e0e0e0',
            color: '#6B705C',
          }}>
            <FormatListBulletedIcon sx={{ color: '#6B705C' }} />
            掃描條碼查詢補登資料
          </DialogTitle>
          <DialogContent sx={{ pt: '24px !important' }}>
            <Stack spacing={2}>
              <TextField
                label="貨櫃號碼"
                value={taskQContainerInput}
                inputProps={{ maxLength: 11 }}
                onChange={(e) => setTaskQContainerInput(e.target.value)}
                fullWidth
                placeholder="輸入或下方掃描"
                InputLabelProps={{ shrink: true }}
              />
              <Stack direction="row" spacing={1.5}>
                <Button
                  fullWidth
                  variant="outlined"
                  startIcon={<CameraAltIcon />}
                  onClick={() => setScannerOpen(true)}
                  sx={{
                    borderColor: '#A5A58D',
                    color: '#6B705C',
                    py: 1.25,
                    fontSize: '1rem',
                    fontWeight: 600,
                    borderRadius: 2,
                    '&:hover': { borderColor: '#8F8F75', backgroundColor: 'rgba(165, 165, 141, 0.08)' },
                  }}
                >
                  掃描
                </Button>
                <Button
                  fullWidth
                  variant="contained"
                  onClick={searchTaskQ}
                  disabled={taskQLoading}
                  sx={{
                    backgroundColor: '#6B705C',
                    py: 1.25,
                    fontSize: '1rem',
                    fontWeight: 700,
                    borderRadius: 2,
                    '&:hover': { backgroundColor: '#5A5F4A' },
                  }}
                >
                  {taskQLoading ? '查詢中…' : '搜尋'}
                </Button>
              </Stack>
              {error && taskQDialogOpen && (
                <Alert severity="error" onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}
              {taskQResults.length > 0 && (
                <List dense sx={{ mt: 1, border: '1px solid #eee', borderRadius: 1 }}>
                  {taskQResults.map((item) => (
                    <ListItemButton key={item.id} onClick={() => pickTaskQ(item)}>
                      <ListItemText
                        primary={`${item.vesselRegNo ?? '-'} / ${item.containerNo ?? '-'}`}
                        secondary={`${item.originLocationShortName || item.originLocation || '-'} → ${item.destinationLocationShortName || item.destinationLocation || '-'}`}
                      />
                    </ListItemButton>
                  ))}
                </List>
              )}
              {!taskQLoading && taskQResults.length === 0 && (
                <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 1 }}>
                  {taskQSearched ? '查無此貨櫃號碼' : '輸入貨櫃號碼後點搜尋'}
                </Typography>
              )}
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setTaskQDialogOpen(false)} sx={{ color: '#666' }}>
              關閉
            </Button>
          </DialogActions>
        </Dialog>

        <BarcodeScanner
          open={scannerOpen}
          onClose={() => setScannerOpen(false)}
          onSuccess={handleScanSuccess}
          title="掃描貨櫃號碼"
        />

        {/* 表單內掃描器 — 一次帶入船掛 + 櫃號 */}
        <BarcodeScanner
          open={formScannerOpen}
          onClose={() => setFormScannerOpen(false)}
          onSuccess={handleFormScanSuccess}
          title="掃描船掛 / 櫃號"
          showVesselRegNo
        />

        {/* 通用「是否繼續」確認 Dialog — 送出前 ISO 6346 檢核碼軟警示 */}
        <Dialog
          open={confirmDialog.open}
          onClose={() => handleConfirmDialogClose(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, color: '#6B705C' }}>
            {confirmDialog.title}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {confirmDialog.text}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => handleConfirmDialogClose(false)} sx={{ color: '#666' }}>
              取消
            </Button>
            <Button
              onClick={() => handleConfirmDialogClose(true)}
              variant="contained"
              sx={{ backgroundColor: '#6B705C', '&:hover': { backgroundColor: '#5A604E' } }}
            >
              確認儲存
            </Button>
          </DialogActions>
        </Dialog>

        {/* 送出失敗強提示 — modal 跳窗，確保在底部按送出的使用者一定看得到結果 */}
        <Dialog
          open={errorDialog !== null}
          onClose={() => setErrorDialog(null)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ fontWeight: 700, color: '#d32f2f', display: 'flex', alignItems: 'center', gap: 1 }}>
            <ErrorOutlineIcon fontSize="small" /> {errorDialog?.title ?? '操作失敗'}
          </DialogTitle>
          <DialogContent>
            <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
              {errorDialog?.message}
            </Typography>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button
              onClick={() => setErrorDialog(null)}
              variant="contained"
              sx={{ backgroundColor: '#6B705C', '&:hover': { backgroundColor: '#5A604E' } }}
            >
              知道了
            </Button>
          </DialogActions>
        </Dialog>

        {/*
          v2.3.x：委託人「選擇方式」selector — 點 input/筆 icon 觸發
          內含兩大按鈕：「📋 從清單選」/「✍ 手動輸入」
        */}
        <Dialog
          open={consignorSelectorOpen}
          onClose={() => setConsignorSelectorOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>選擇委託人</DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Button
                fullWidth
                size="large"
                variant="outlined"
                onClick={() => {
                  setConsignorSelectorOpen(false)
                  setConsignorPickerOpen(true)
                }}
                sx={{
                  py: 2,
                  fontSize: '1rem',
                  color: '#6B705C',
                  borderColor: '#6B705C',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  px: 2.5,
                }}
              >
                📋 從清單選
              </Button>
              <Button
                fullWidth
                size="large"
                variant="outlined"
                onClick={() => {
                  setConsignorSelectorOpen(false)
                  setManualConsignorDraft(form.billingRequester)
                  setManualConsignorOpen(true)
                }}
                sx={{
                  py: 2,
                  fontSize: '1rem',
                  color: '#6B705C',
                  borderColor: '#6B705C',
                  justifyContent: 'flex-start',
                  textAlign: 'left',
                  px: 2.5,
                }}
              >
                ✍ 手動輸入
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setConsignorSelectorOpen(false)} sx={{ color: '#666' }}>
              取消
            </Button>
          </DialogActions>
        </Dialog>

        {/* v2.3：委託人「從清單選」MobilePicker */}
        <MobilePicker
          open={consignorPickerOpen}
          title="選擇委託人"
          options={consignorOptions}
          value={form.billingRequester}
          onChange={(v) => setForm((f) => ({ ...f, billingRequester: v }))}
          onClose={() => setConsignorPickerOpen(false)}
          emptyHint="尚無委託人資料"
        />

        {/*
          v2.3.x：拖運日期「選擇方式」selector — 點 input 觸發
          📅 自己選日期 / 📋 從過往打卡日期選
        */}
        <Dialog
          open={dutyDateSelectorOpen}
          onClose={() => setDutyDateSelectorOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>選擇拖運日期</DialogTitle>
          <DialogContent sx={{ pb: 1 }}>
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              <Button
                fullWidth
                size="large"
                variant="outlined"
                onClick={() => {
                  setDutyDateSelectorOpen(false)
                  setManualDutyDateDraft(form.dutyDate || todayStr())
                  setManualDutyDateOpen(true)
                }}
                sx={{ py: 2, fontSize: '1rem', color: '#6B705C', borderColor: '#6B705C',
                     justifyContent: 'flex-start', textAlign: 'left', px: 2.5 }}
              >
                📅 自己選日期
              </Button>
              <Button
                fullWidth
                size="large"
                variant="outlined"
                onClick={() => {
                  setDutyDateSelectorOpen(false)
                  openPicker()  // 既有打卡 picker dialog（pickerOpen 控制）
                }}
                sx={{ py: 2, fontSize: '1rem', color: '#6B705C', borderColor: '#6B705C',
                     justifyContent: 'flex-start', textAlign: 'left', px: 2.5 }}
              >
                📋 從過往打卡日期選
              </Button>
            </Stack>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setDutyDateSelectorOpen(false)} sx={{ color: '#666' }}>
              取消
            </Button>
          </DialogActions>
        </Dialog>

        {/* v2.3.x：拖運日期「自己選」— 簡單 date input dialog */}
        <Dialog
          open={manualDutyDateOpen}
          onClose={() => setManualDutyDateOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>選擇拖運日期</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              type="date"
              margin="dense"
              label="拖運日期"
              value={manualDutyDateDraft}
              onChange={(e) => setManualDutyDateDraft(e.target.value)}
              InputLabelProps={{ shrink: true }}
              helperText="自選日期將不連結至打卡紀錄"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setManualDutyDateOpen(false)} sx={{ color: '#666' }}>取消</Button>
            <Button
              onClick={() => {
                // 手選日期：清掉 dailyReportId，保留現有 trailerNo
                setForm((f) => ({ ...f, dutyDate: manualDutyDateDraft, dailyReportId: '' }))
                setPickedDailyReportDisplay('')
                setManualDutyDateOpen(false)
              }}
              variant="contained"
              sx={{ backgroundColor: '#6B705C', '&:hover': { backgroundColor: '#5A604E' } }}
            >
              確定
            </Button>
          </DialogActions>
        </Dialog>

        {/* v2.3.x：車輛 MobilePicker — 司機所屬公司全部車輛 */}
        <MobilePicker
          open={vehiclePickerOpen}
          title="選擇車號"
          options={vehicleOptions}
          value={form.trailerNo}
          onChange={(v) => setForm((f) => ({ ...f, trailerNo: v }))}
          onClose={() => setVehiclePickerOpen(false)}
          emptyHint="無可選車輛"
        />

        {/* v2.3：委託人「手動輸入」dialog */}
        <Dialog
          open={manualConsignorOpen}
          onClose={() => setManualConsignorOpen(false)}
          fullWidth
          maxWidth="xs"
        >
          <DialogTitle>手動輸入委託人</DialogTitle>
          <DialogContent>
            <TextField
              autoFocus
              fullWidth
              margin="dense"
              label="委託人代碼/名稱"
              value={manualConsignorDraft}
              onChange={(e) => setManualConsignorDraft(e.target.value.slice(0, 60))}
              placeholder="輸入後按確定"
              inputProps={{ maxLength: 60 }}
              helperText="若系統內無對應，可手動輸入"
            />
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 2 }}>
            <Button onClick={() => setManualConsignorOpen(false)} sx={{ color: '#666' }}>
              取消
            </Button>
            <Button
              onClick={() => {
                setForm((f) => ({ ...f, billingRequester: manualConsignorDraft.trim() }))
                setManualConsignorOpen(false)
              }}
              variant="contained"
              sx={{ backgroundColor: '#6B705C', '&:hover': { backgroundColor: '#5A604E' } }}
            >
              確定
            </Button>
          </DialogActions>
        </Dialog>
      </Container>
    </Box>
  )
}

export default MissionAmendmentPage
